import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Gift,
  Lock,
  CheckCircle2,
  Trophy,
  Star,
  Crown,
  Package,
  Phone,
  Truck,
  Sparkles,
  ChevronRight,
} from 'lucide-react';

const RewardsPage = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  
  const [rewards, setRewards] = useState([]);
  const [levelInfo, setLevelInfo] = useState(null);
  const [xpHistory, setXpHistory] = useState([]);
  const [dailyUsage, setDailyUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claimDialog, setClaimDialog] = useState({ open: false, reward: null });
  const [claimForm, setClaimForm] = useState({
    address: '',
    recipient_email: '',
    recipient_name: '',
    notes: '',
  });
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rewardsRes, levelRes, historyRes, usageRes] = await Promise.all([
        api.get('/gamification/rewards'),
        api.get('/gamification/my-level'),
        api.get('/xp/history?limit=30').catch(() => ({ data: [] })),
        api.get('/xp/daily-usage').catch(() => ({ data: [] })),
      ]);
      setRewards(rewardsRes.data);
      setLevelInfo(levelRes.data);
      setXpHistory(historyRes.data || []);
      setDailyUsage(usageRes.data || []);
    } catch (error) {
      console.error('Error fetching rewards:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = (reward) => {
    setClaimForm({
      address: '',
      recipient_email: '',
      recipient_name: '',
      notes: '',
    });
    setClaimDialog({ open: true, reward });
  };

  const submitClaim = async () => {
    const reward = claimDialog.reward;
    if (!reward) return;

    // Validate
    if (reward.requires_address && !claimForm.address) {
      toast.error('Adresse postale requise');
      return;
    }
    if (reward.requires_recipient && (!claimForm.recipient_email || !claimForm.recipient_name)) {
      toast.error('Informations du destinataire requises');
      return;
    }

    setClaiming(true);
    try {
      await api.post('/gamification/rewards/claim', {
        level: reward.level,
        ...claimForm,
      });
      toast.success('🎉 Récompense réclamée ! L\'équipe te contactera bientôt.');
      setClaimDialog({ open: false, reward: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setClaiming(false);
    }
  };

  const getRewardIcon = (reward) => {
    switch (reward.type) {
      case 'physical':
      case 'physical_and_badge':
      case 'physical_and_frame':
        return <Package className="w-5 h-5" />;
      case 'coaching':
      case 'coaching_and_title':
        return <Phone className="w-5 h-5" />;
      case 'gift':
        return <Gift className="w-5 h-5" />;
      case 'ultimate':
        return <Crown className="w-5 h-5" />;
      default:
        return <Star className="w-5 h-5" />;
    }
  };

  const getFrameStyle = (frame) => {
    switch (frame) {
      case 'bronze':
        return 'border-amber-700 bg-gradient-to-br from-amber-700/20 to-amber-900/20';
      case 'silver':
        return 'border-gray-400 bg-gradient-to-br from-gray-300/20 to-gray-500/20';
      case 'gold':
        return 'border-yellow-500 bg-gradient-to-br from-yellow-400/20 to-orange-500/20';
      case 'diamond':
        return 'border-cyan-400 bg-gradient-to-br from-cyan-300/20 to-blue-500/20 animate-pulse';
      default:
        return 'border-border';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Gift className="w-12 h-12 text-primary animate-bounce mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement des récompenses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="rewards-page">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center">
                <Gift className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-xl">Récompenses</h1>
                <p className="text-sm text-muted-foreground">Débloque des cadeaux en progressant</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Level Progress Card */}
        {levelInfo && (
          <Card className="card-base mb-8 overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-yellow-500/10" />
            <CardContent className="p-6 relative">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Trophy className="w-5 h-5 text-yellow-500" />
                    <span className="text-2xl font-bold">Niveau {levelInfo.level}</span>
                  </div>
                  <p className="text-muted-foreground">{levelInfo.title}</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-primary">{levelInfo.points?.toLocaleString()}</div>
                  <div className="text-sm text-muted-foreground">XP total</div>
                </div>
              </div>
              
              {levelInfo.xp_next_level && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Progression vers niveau {levelInfo.level + 1}</span>
                    <span className="font-medium">{levelInfo.progress_percent}%</span>
                  </div>
                  <Progress value={levelInfo.progress_percent} className="h-3" />
                  <p className="text-xs text-muted-foreground text-right">
                    {(levelInfo.xp_next_level - levelInfo.points).toLocaleString()} XP restants
                  </p>
                </div>
              )}

              {levelInfo.pending_rewards > 0 && (
                <div className="mt-4 p-3 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
                  <p className="text-sm font-medium text-yellow-500 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    {levelInfo.pending_rewards} récompense(s) à réclamer !
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* XP Journal + daily caps (transparency) */}
        <div className="grid gap-4 md:grid-cols-2 mb-8">
          <Card className="card-base" data-testid="xp-journal-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                Journal XP
              </CardTitle>
              <CardDescription className="text-xs">D'où vient chaque XP gagnée</CardDescription>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {xpHistory.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucune XP enregistrée pour l'instant.</p>
              ) : (
                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                  {xpHistory.map((entry) => (
                    <div
                      key={entry.id}
                      className="flex items-center justify-between gap-3 text-sm py-1.5 border-b border-border/60 last:border-0"
                      data-testid={`xp-entry-${entry.id}`}
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium text-xs">{entry.label || entry.source}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {new Date(entry.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          {entry.reason ? ` · ${entry.reason}` : ''}
                        </p>
                      </div>
                      <span className={`font-bold text-sm flex-shrink-0 ${entry.amount >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {entry.amount >= 0 ? '+' : ''}{entry.amount}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="card-base" data-testid="xp-caps-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Trophy className="w-4 h-4 text-primary" />
                Plafonds du jour
              </CardTitle>
              <CardDescription className="text-xs">
                Anti-abus : chaque source d'XP a une limite quotidienne. Les jalons et récompenses restent illimités.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="space-y-2.5 max-h-72 overflow-y-auto pr-1">
                {dailyUsage.map((usage) => (
                  <div key={usage.source} data-testid={`xp-cap-${usage.source}`}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-muted-foreground">{usage.label}</span>
                      <span className="font-medium">{usage.earned_today}/{usage.daily_cap} XP</span>
                    </div>
                    <Progress value={(usage.earned_today / usage.daily_cap) * 100} className="h-1.5" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Rewards List */}
        <div className="space-y-4">
          <h2 className="font-heading text-xl flex items-center gap-2">
            <Gift className="w-5 h-5 text-primary" />
            Toutes les récompenses
          </h2>

          <div className="grid gap-4">
            {rewards.map((reward) => {
              const isUnlocked = reward.is_unlocked;
              const isCosmetic = ['badge', 'title', 'badge_and_frame'].includes(reward.type);
              const status = reward.claim_status; // null | 'pending' | 'claimed' | 'fulfilled' | 'auto'

              return (
                <Card 
                  key={reward.level}
                  className={`card-base transition-all ${getFrameStyle(reward.frame)} ${
                    isUnlocked ? 'opacity-100' : 'opacity-60'
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Level Badge */}
                      <div className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center flex-shrink-0 ${
                        isUnlocked 
                          ? 'bg-gradient-to-br from-primary/20 to-yellow-500/20' 
                          : 'bg-secondary'
                      }`}>
                        <span className="text-xs text-muted-foreground">Niv</span>
                        <span className={`text-xl font-bold ${isUnlocked ? 'text-primary' : 'text-muted-foreground'}`}>
                          {reward.level}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 flex-wrap sm:flex-nowrap">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-1 min-w-0">
                              <span className="text-2xl shrink-0">{reward.icon}</span>
                              <h3 className="font-semibold break-words">{reward.title}</h3>
                            </div>
                            <p className="text-sm text-muted-foreground break-words">{reward.description}</p>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <Badge variant="outline" className="text-xs">
                                {reward.level_title}
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                {reward.xp_required.toLocaleString()} XP
                              </Badge>
                              {reward.value > 0 && (
                                <Badge className="bg-green-500/20 text-green-500 text-xs">
                                  ~{reward.value}€
                                </Badge>
                              )}
                            </div>
                          </div>

                          {/* Status / Action */}
                          <div className="flex-shrink-0 max-w-full">
                            {!isUnlocked && (
                              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                                <Lock className="w-5 h-5 text-muted-foreground" />
                              </div>
                            )}
                            {isUnlocked && isCosmetic && (
                              <div className="flex flex-col items-center gap-1" data-testid={`reward-auto-${reward.level}`}>
                                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                                </div>
                                <span className="text-[10px] text-green-500 font-medium">Automatique</span>
                              </div>
                            )}
                            {isUnlocked && !isCosmetic && status === 'claimed' && (
                              <Badge className="bg-amber-500/20 text-amber-600" data-testid={`reward-processing-${reward.level}`}>
                                📦 En cours de traitement
                              </Badge>
                            )}
                            {isUnlocked && !isCosmetic && status === 'fulfilled' && (
                              <div className="flex flex-col items-center gap-1" data-testid={`reward-fulfilled-${reward.level}`}>
                                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                                </div>
                                <span className="text-[10px] text-green-500 font-medium">Reçue</span>
                              </div>
                            )}
                            {isUnlocked && !isCosmetic && reward.membership_locked && status !== 'claimed' && status !== 'fulfilled' && (
                              <div className="flex flex-col items-end gap-1 max-w-[190px] text-right" data-testid={`reward-membership-lock-${reward.level}`}>
                                <Badge variant="outline" className="border-amber-500/40 text-amber-600">
                                  <Lock className="w-3 h-3 mr-1" />
                                  Dans {reward.membership_days_remaining} jour{reward.membership_days_remaining > 1 ? 's' : ''}
                                </Badge>
                                <span className="text-[10px] text-muted-foreground leading-tight">
                                  Les cadeaux se débloquent après 30 jours dans l'équipage
                                </span>
                              </div>
                            )}
                            {isUnlocked && !isCosmetic && !reward.membership_locked && status !== 'claimed' && status !== 'fulfilled' && (
                              <Button 
                                size="sm" 
                                onClick={() => handleClaim(reward)}
                                className="bg-primary hover:bg-primary/90"
                                data-testid={`reward-claim-btn-${reward.level}`}
                              >
                                Réclamer
                                <ChevronRight className="w-4 h-4 ml-1" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </main>

      {/* Claim Dialog */}
      <Dialog open={claimDialog.open} onOpenChange={(open) => setClaimDialog({ open, reward: open ? claimDialog.reward : null })}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {claimDialog.reward?.icon} Réclamer ta récompense
            </DialogTitle>
            <DialogDescription>
              {claimDialog.reward?.title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {claimDialog.reward?.requires_address && (
              <div className="space-y-2">
                <Label>Adresse postale complète *</Label>
                <Textarea
                  value={claimForm.address}
                  onChange={(e) => setClaimForm({ ...claimForm, address: e.target.value })}
                  placeholder="Nom, Rue, Code postal, Ville, Pays"
                  className="bg-secondary/50"
                  rows={3}
                />
              </div>
            )}

            {claimDialog.reward?.requires_recipient && (
              <>
                <div className="space-y-2">
                  <Label>Nom du destinataire *</Label>
                  <Input
                    value={claimForm.recipient_name}
                    onChange={(e) => setClaimForm({ ...claimForm, recipient_name: e.target.value })}
                    placeholder="Prénom Nom de la personne"
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email du destinataire *</Label>
                  <Input
                    type="email"
                    value={claimForm.recipient_email}
                    onChange={(e) => setClaimForm({ ...claimForm, recipient_email: e.target.value })}
                    placeholder="email@exemple.com"
                    className="bg-secondary/50"
                  />
                </div>
              </>
            )}

            {claimDialog.reward?.requires_booking && (
              <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                <p className="text-sm text-blue-400">
                  📅 L'équipe te contactera par email pour planifier ton créneau de coaching avec Ismaël.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes (optionnel)</Label>
              <Textarea
                value={claimForm.notes}
                onChange={(e) => setClaimForm({ ...claimForm, notes: e.target.value })}
                placeholder="Instructions particulières..."
                className="bg-secondary/50"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setClaimDialog({ open: false, reward: null })}>
              Annuler
            </Button>
            <Button onClick={submitClaim} disabled={claiming}>
              {claiming ? 'Envoi...' : 'Confirmer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RewardsPage;
